/**
 * How each school-editable field reaches families, per release line (release
 * lines U7, R11, R12, KTD5).
 *
 * A school saves every field directly. Whether that save is live, or a draft
 * that waits for staff, depends on the field AND on the line the app runs:
 * the 1.9 bundle reads the welcome slides and the pre-login photos at runtime,
 * the 1.8 bundle carries them. So the answer is a function of a line's
 * CAPABILITIES, and a line's capabilities come from its runtime through one
 * small table below, never from per-line branches in the callers.
 *
 * The classes are the asset slot delivery vocabulary (`AssetSlotDelivery`),
 * not a parallel one:
 *
 *  - `live`          read at runtime by the app's line; live when saved (R12).
 *  - `ota`           carried by the JS bundle; an over-the-air update from the
 *                    app's line takes it to installed apps.
 *  - `store-build`   baked into the binary, or shown to a new install before any
 *                    update can apply; needs the next app build.
 *  - `store-listing` the store listing text; ships with the next store
 *                    submission (Apple) or listing publish (Play).
 *
 * WHAT THE CODE DOES, BY LINE (read 2026-09-24 from `release/mobile-1.8` and
 * `dev`):
 *
 *  - Logos: both lines read them at runtime (the pre-login logo lane and the
 *    app config), so they are live everywhere.
 *  - Login text: both lines read the pre-login copy from the registry cache on
 *    the next cold start, so it is live everywhere.
 *  - Welcome text and slide photos: 1.9 reads them through the pre-login
 *    onboarding lane. On 1.8 the slide photos are bundled (`getSchoolAsset`),
 *    and the welcome carousel is what a NEW install sees on its first open,
 *    which runs the embedded bundle, so the existing build-wait verdict
 *    (`derive-school-app-build-status.ts`) already treats both as waiting on a
 *    build. `store-build` on 1.8.
 *  - Login photo (`login-bg`): 1.9 reads it through the same pre-login lane. On
 *    1.8 it is BUNDLED too (`use-login-controller.ts` reads it with
 *    `getSchoolAsset`), which the plan's assumption did not expect. The login
 *    screen is seen on every signed-out open, not only the first, and an OTA
 *    re-snapshots the school's runtime art into its bundle, so an OTA from the
 *    1.8 line does reach installed apps with it: `ota` on 1.8. This is the only
 *    `ota` field on either line.
 *  - App name, icon, splash, adaptive icon: native build inputs; an OTA cannot
 *    replace them. `store-build` everywhere.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-09-23-2135-feat-app-release-lines-and-school-drafts-plan.md (U7)
 */

import { z } from 'zod'
import type { AssetSlotDelivery } from './mobile-app-asset'
import type { StoreListingStore } from './mobile-app-store-listing'
import { compareLineRuntimes } from './release-line'
import type { SchoolAppFieldKey } from './school-app-field-draft'

// -----------------------------------------------------------------------------
// Line capabilities
// -----------------------------------------------------------------------------

/** What a line's bundle reads at runtime, as far as school fields are concerned. */
export interface ReleaseLineCapabilities {
  /** The welcome carousel's text and slide photos come from the pre-login onboarding lane. */
  readsWelcomeContentAtRuntime: boolean
  /** The login background photo comes from the pre-login onboarding lane. */
  readsLoginPhotoAtRuntime: boolean
}

/**
 * The first runtime with each capability. Adding a line changes nothing here;
 * a capability a new runtime gains is one new key or one moved version.
 */
export const RELEASE_LINE_CAPABILITY_SINCE: Readonly<Record<keyof ReleaseLineCapabilities, string>> = {
  readsWelcomeContentAtRuntime: '1.9.0',
  readsLoginPhotoAtRuntime: '1.9.0'
}

/**
 * The capabilities of the line with this runtime. A null runtime (no line to
 * read from) has none: nothing can be called live on a line nobody can name.
 */
export function releaseLineCapabilities(runtimeVersion: string | null): ReleaseLineCapabilities {
  const has = (since: string) => runtimeVersion !== null && compareLineRuntimes(runtimeVersion, since) >= 0
  return {
    readsWelcomeContentAtRuntime: has(RELEASE_LINE_CAPABILITY_SINCE.readsWelcomeContentAtRuntime),
    readsLoginPhotoAtRuntime: has(RELEASE_LINE_CAPABILITY_SINCE.readsLoginPhotoAtRuntime)
  }
}

// -----------------------------------------------------------------------------
// The field catalog
// -----------------------------------------------------------------------------

/** The delivery classes a school field can have: the slot vocabulary minus `none`. */
export const SCHOOL_APP_FIELD_DELIVERY_CLASSES = [
  'live',
  'ota',
  'store-build',
  'store-listing'
] as const satisfies readonly AssetSlotDelivery[]
export const SchoolAppFieldDeliveryClassSchema = z.enum(SCHOOL_APP_FIELD_DELIVERY_CLASSES)
export type SchoolAppFieldDeliveryClass = z.infer<typeof SchoolAppFieldDeliveryClassSchema>

/** The classes a draft can have: everything that is not live on save. */
export const SCHOOL_APP_FIELD_DRAFT_CLASSES = [
  'ota',
  'store-build',
  'store-listing'
] as const satisfies readonly Exclude<SchoolAppFieldDeliveryClass, 'live'>[]
export const SchoolAppFieldDraftClassSchema = z.enum(SCHOOL_APP_FIELD_DRAFT_CLASSES)
export type SchoolAppFieldDraftClass = z.infer<typeof SchoolAppFieldDraftClassSchema>

/**
 * How a draft will reach families, in the words the school editor shows beside
 * it (R11).
 */
export const SCHOOL_APP_FIELD_DRAFT_LABELS: Record<SchoolAppFieldDraftClass, string> = {
  ota: 'Next app update',
  'store-build': 'Next app build',
  'store-listing': 'Next store submission'
}

/** The listing columns a store-text field compares, per store. Absent = the store has no such field. */
export type StoreTextColumn =
  | 'title'
  | 'subtitle'
  | 'shortDescription'
  | 'description'
  | 'keywords'
  | 'promotionalText'
  | 'marketingUrl'
  | 'supportUrl'
  | 'privacyPolicyUrl'

type FieldRule =
  | { kind: 'fixed'; deliveryClass: SchoolAppFieldDeliveryClass }
  | { kind: 'store-text'; columns: Partial<Record<StoreListingStore, StoreTextColumn>> }
  | { kind: 'by-line'; deliveryClass: (capabilities: ReleaseLineCapabilities) => SchoolAppFieldDeliveryClass }

const welcomeContent: FieldRule = {
  kind: 'by-line',
  deliveryClass: capabilities => (capabilities.readsWelcomeContentAtRuntime ? 'live' : 'store-build')
}

/**
 * Every school-editable field (U6's keys) and how it ships. `app-name` is the
 * on-device label (`MobileApp.name`), a build input; `store-title` is the
 * listing name. `store-subtitle` is Apple's subtitle and, on Play, the short
 * description, the listing's one-line summary under the name.
 */
const FIELD_RULES: Record<SchoolAppFieldKey, FieldRule> = {
  'app-name': { kind: 'fixed', deliveryClass: 'store-build' },
  'store-title': { kind: 'store-text', columns: { apple: 'title', play: 'title' } },
  'store-subtitle': { kind: 'store-text', columns: { apple: 'subtitle', play: 'shortDescription' } },
  'store-description': { kind: 'store-text', columns: { apple: 'description', play: 'description' } },
  'store-keywords': { kind: 'store-text', columns: { apple: 'keywords' } },
  'store-promotional-text': { kind: 'store-text', columns: { apple: 'promotionalText' } },
  'store-marketing-url': { kind: 'store-text', columns: { apple: 'marketingUrl', play: 'marketingUrl' } },
  'store-support-url': { kind: 'store-text', columns: { apple: 'supportUrl', play: 'supportUrl' } },
  'store-privacy-policy-url': {
    kind: 'store-text',
    columns: { apple: 'privacyPolicyUrl', play: 'privacyPolicyUrl' }
  },
  'welcome-text': welcomeContent,
  'login-text': { kind: 'fixed', deliveryClass: 'live' },
  'onboard-1': welcomeContent,
  'onboard-2': welcomeContent,
  'onboard-3': welcomeContent,
  'login-bg': {
    kind: 'by-line',
    deliveryClass: capabilities => (capabilities.readsLoginPhotoAtRuntime ? 'live' : 'ota')
  },
  'logo-small-light': { kind: 'fixed', deliveryClass: 'live' },
  'logo-small-dark': { kind: 'fixed', deliveryClass: 'live' },
  'main-logo-light': { kind: 'fixed', deliveryClass: 'live' },
  'main-logo-dark': { kind: 'fixed', deliveryClass: 'live' },
  'app-icon': { kind: 'fixed', deliveryClass: 'store-build' },
  splash: { kind: 'fixed', deliveryClass: 'store-build' },
  'adaptive-icon-foreground': { kind: 'fixed', deliveryClass: 'store-build' }
}

/** How a field reaches families on a line with these capabilities. */
export function fieldDeliveryClass(
  fieldKey: SchoolAppFieldKey,
  capabilities: ReleaseLineCapabilities
): SchoolAppFieldDeliveryClass {
  const rule = FIELD_RULES[fieldKey]
  if (rule.kind === 'fixed') return rule.deliveryClass
  if (rule.kind === 'store-text') return 'store-listing'
  return rule.deliveryClass(capabilities)
}

/**
 * The listing column a store-text field compares on one store, or null when
 * the field is not store text or that store has no such field (Play has no
 * keywords), in which case the field has no status on that platform.
 */
export function storeTextColumn(fieldKey: SchoolAppFieldKey, store: StoreListingStore): StoreTextColumn | null {
  const rule = FIELD_RULES[fieldKey]
  return rule.kind === 'store-text' ? (rule.columns[store] ?? null) : null
}
